// bizSAFE Pipeline — Phase 1 pure selector.
// Derives one row per ACTIVE client/cycle from existing data (clients, quotations, jobs,
// renewal_reminders_sent) + the manual pipeline_meta. No inference of structure; auto-first.
//
// De-duplication rule (plain English):
//   One active client/cycle appears as exactly ONE row. All quote revisions (r1, r2 …), the signed
//   quote, and the resulting job collapse into that single row — same item at different stages.
//   Renewals are keyed by the client; new applications by the quote family; once signed, the item is
//   keyed by the client. Lost (Rejected / Closed down) and completed cycles leave the active board.

import { resolveExpiry } from './expiry'
import { quoteCompanyName } from './quoteTitle'
import { isOtherService } from './service'

export const PIPELINE_HORIZON_MONTHS = 6          // look-ahead window (single configurable constant)
export const REMINDER_NO_OUTCOME_DAYS = 14        // N: reminder sent but still no sales outcome
export const SALES_OUTCOMES = ['Open', 'Rejected', 'Closed down', 'On hold', 'No response']
export const LOST_OUTCOMES = ['Rejected', 'Closed down']   // these leave the active board ('No response' and 'On hold' stay visible — they only stop the dashboard red)

const DAY = 86400000

// Mirror of Quotations.familyKey: collapse q/<n>r<rev>-… revisions to one family.
export function familyKey(quoteRef) {
  return String(quoteRef || '').replace(/^([a-z]+\/\d+)r\d+(-.*)$/, '$1$2')
}
// Which quote a client's row follows: open (Draft / Sent) first, then the newest.
const quoteRank = q => (['Draft', 'Sent'].includes(q.status) ? 1e15 : 0) + (Date.parse(q.created_at || q.signed_date || q.sent_date || 0) || 0)
function revNum(quoteRef) { const m = String(quoteRef || '').match(/r(\d+)/); return m ? parseInt(m[1], 10) : 0 }
function daysBetween(a, b) { return Math.floor((a - b) / DAY) }

// 5 high-level milestones only — scannable. "Quote created" (draft) folds into Awaiting quote;
// "Plan sent" folds into Scheduled (fine detail lives on the Job page).
export const STAGES = ['Awaiting quote', 'Quoted', 'Signed', 'Scheduled', 'Audit done']
function stageRank(s) { return STAGES.indexOf(s) }

function computeStage(quote, job, today) {
  if (job && job.audit_date) {
    const d = new Date(job.audit_date)
    if (d < today) return 'Audit done'
    return 'Scheduled'
  }
  // A job exists but isn't scheduled yet → the client has COMMITTED (signed). Jobs are only ever created on
  // commitment — online signing, "Mark signed", or a deliberate manual job — so a job's existence is itself
  // proof of "Signed", independent of whether a signed quotation is present (covers manual/offline sign-ins).
  if (job) return 'Signed'
  if (quote) {
    if (quote.status === 'Cancelled') return 'Awaiting quote'                 // signed, then cancelled
    if (quote.status === 'Signed' || quote.signed_date) return 'Signed'
    if (quote.status === 'Sent' || quote.sent_date) return 'Quoted'
  }
  return 'Awaiting quote'
}

// Phase-1 post-audit chip — only what existing data can prove (richer states ship with the Job section).
function computePostAudit(job, today) {
  if (!job || !job.audit_date) return ''
  if (new Date(job.audit_date) >= today) return ''
  return job.report_submitted ? 'Report received' : 'Awaiting report'
}

function monthKey(dateStr) {
  if (!dateStr) return null
  const d = new Date(dateStr)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

// inputs: { clients, quotations, jobs, reminders, meta } — raw row arrays. now: Date (testable).
export function derivePipeline({ clients = [], quotations: allQuotations = [], jobs: allJobs = [], reminders = [], meta = [] }, now = new Date()) {
  // The bizSAFE Tracker never sees CLIENT INTERNAL AUDIT work — otherwise an internal audit job could make a
  // bizSAFE renewal look scheduled. Written as "leave out internal audit" so bizSAFE + SHMS rows are unchanged.
  const quotations = allQuotations.filter(q => !isOtherService(q.audit_type))
  // A cancelled job is not booked work: it never makes a client look signed / scheduled (its cancelled quotation
  // counts the cycle as lost below).
  const jobs = allJobs.filter(j => !isOtherService(j.audit_type) && j.status !== 'Cancelled')
  const today = now
  const horizonDays = Math.round(PIPELINE_HORIZON_MONTHS * 30.4)
  const clientById = new Map(clients.map(c => [c.id, c]))
  // Client-list identity: resolve a quote to a CLIENT record even when it was never linked (client_id null) —
  // e.g. the office keyed the client in separately (manual sign-in) before/after the quote. Match by UEN, then
  // company name. Normalised so "Pte. Ltd." vs "Pte Ltd" and spacing/case never break the match.
  const norm = s => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '')
  const clientByUEN = new Map(), clientByName = new Map()
  for (const c of clients) { const u = norm(c.uen); if (u) clientByUEN.set(u, c); const n = norm(c.name); if (n) clientByName.set(n, c) }
  const resolveClientForQuote = (quote) => {
    if (!quote) return null
    const r = quote.form_responses || {}
    const uen = norm(quote.prospect_uen || r.uen || r.UEN || r.company_uen || r['Company UEN'] || r['UEN (Unique Entity Number)'])
    if (uen && clientByUEN.has(uen)) return clientByUEN.get(uen)
    const name = norm(quoteCompanyName(quote, null))
    return (name && clientByName.get(name)) || null
  }
  // Non-primary members of a combined audit are carried by the PRIMARY job — suppress their standalone row so a
  // linked (secondary) company never appears as an independent outstanding item (dashboard OR tracker),
  // regardless of how the quote was signed (manual "Mark signed" or online).
  const suppressedClients = new Set((jobs || []).filter(j => j.combined_group_id && !j.is_combined_primary && j.client_id).map(j => j.client_id))
  const metaByKey = new Map(meta.map(m => [m.key, m]))
  const reminderByClient = new Map()
  for (const r of reminders) {
    const prev = reminderByClient.get(r.client_id)
    if (!prev || new Date(r.created_at) > new Date(prev.created_at)) reminderByClient.set(r.client_id, r)
  }

  // Latest revision per quote family.
  const latestQuoteByFamily = new Map()
  for (const q of quotations) {
    const fk = familyKey(q.quote_ref || q.id)
    const cur = latestQuoteByFamily.get(fk)
    if (!cur || revNum(q.quote_ref) >= revNum(cur.quote_ref)) latestQuoteByFamily.set(fk, q)
  }
  // Most-advanced job per client (prefer one with an audit_date).
  const jobByClient = new Map()
  for (const j of jobs) {
    if (!j.client_id) continue
    const cur = jobByClient.get(j.client_id)
    if (!cur || (j.audit_date && !cur.audit_date) || (j.audit_date && cur.audit_date && new Date(j.audit_date) > new Date(cur.audit_date))) {
      jobByClient.set(j.client_id, j)
    }
  }

  const items = new Map() // key -> { clientId, quote, job, family }

  // 1) Renewals: clients with an expiry inside [-horizon, +horizon] (next 6 months + overdue).
  for (const c of clients) {
    if (!c.bizsafe_expiry_date) continue
    const dte = daysBetween(new Date(c.bizsafe_expiry_date), today)
    if (dte > horizonDays || dte < -horizonDays) continue
    items.set(`C:${c.id}`, { clientId: c.id, quote: null, job: jobByClient.get(c.id) || null, family: null })
  }

  // 1b) Committed work (jobs): a job for a client in the list is real business and must appear — even with NO
  //     AuditPM quote and no expiry. This covers the manual sign-in flow: the office keys the client in, then
  //     creates the job directly (no quote). Without this such clients never became a pipeline row.
  for (const j of jobs) {
    if (!j.client_id || j.status === 'Completed') continue   // completed cycles surface via the renewal (expiry) path
    const key = `C:${j.client_id}`
    if (!items.has(key)) items.set(key, { clientId: j.client_id, quote: null, job: jobByClient.get(j.client_id) || j, family: null })
  }

  // 2) New applications / quotes: collapse by family, key by client (linked OR identity-matched) else by family.
  for (const [fk, q] of latestQuoteByFamily) {
    const status = q.status
    if (['Superseded', 'Withdrawn', 'Expired'].includes(status)) continue // not active
    // Resolve to a client: explicit link first, else UEN/name match to a client record. An unlinked, unmatched
    // quote stays a prospect (Q:) and is filtered off the client-list board.
    const matched = (q.client_id ? clientById.get(q.client_id) : null) || resolveClientForQuote(q)
    const clientId = matched?.id || null
    const key = clientId ? `C:${clientId}` : `Q:${fk}`
    const existing = items.get(key)
    if (existing) {
      // Several quote families for one client (e.g. last cycle's signed quote + this cycle's): an open quote (Draft /
      // Sent) wins, else the newest — so a cancelled contract counts this cycle as lost, and never hides a newer
      // live quote. (Before, the order the rows happened to arrive in decided.)
      if (existing.quote && quoteRank(existing.quote) > quoteRank(q)) continue
      existing.quote = q; existing.family = fk
      if (clientId && !existing.job) existing.job = jobByClient.get(clientId) || null
    } else {
      items.set(key, { clientId, quote: q, job: clientId ? (jobByClient.get(clientId) || null) : null, family: fk })
    }
  }

  // 3) Build rows.
  const rows = []
  for (const [key, it] of items) {
    const client = it.clientId ? clientById.get(it.clientId) : null
    const quote = it.quote
    const job = it.job
    const m = metaByKey.get(key) ||
      (it.clientId && it.family ? metaByKey.get(`Q:${it.family}`) : null) || {} // dual-key fallback (Q->C survives signing)

    // Renewal vs New from the single source of truth (bizSAFE expiry), not application_type.
    const expiryDate = resolveExpiry(client, quote?.form_responses)
    const type = expiryDate ? 'Renewal' : 'New'
    // A cert was issued for this cycle (job Completed) → the row now tracks the NEXT renewal, which hasn't
    // started. Don't let the finished cycle's job/quote drive the columns: show "Awaiting renewal" + a subtle
    // last-audited reference, clear the (stale) upcoming-audit date / post-audit / auto-won outcome.
    const certified = !!job && (job.status === 'Completed' || job.workflow_state === 'Completed')
    const stage = certified ? 'Awaiting renewal' : computeStage(quote, job, today)
    const postAudit = certified ? '' : computePostAudit(job, today)
    const auditDate = certified ? null : (job?.audit_date || null)
    const lastAuditDate = certified ? (job?.audit_date || null) : null

    // Sales outcome: manual (meta) > auto-lost (declined quote) > auto-positive (stage) > Open.
    let salesOutcome = (m.sales_outcome && m.sales_outcome !== 'Open') ? m.sales_outcome : null
    // A manual outcome belongs to the CYCLE it was set in. Once a LATER audit completes (the row rolls to
    // 'Awaiting renewal' for the next cycle), an outcome recorded before that audit is stale — the client
    // plainly came back. Auto-expire it so last cycle's "On hold / No response" can never silently hide
    // the NEXT renewal from the at-risk radar.
    if (salesOutcome && certified && m.sales_outcome_at && job?.audit_date
        && new Date(m.sales_outcome_at) < new Date(job.audit_date)) salesOutcome = null
    let outcomeAuto = false
    // Declined, or signed then cancelled → lost for this cycle (auto).
    if (!salesOutcome && quote && (quote.status === 'Declined' || quote.status === 'Cancelled')) { salesOutcome = 'Rejected'; outcomeAuto = true }

    const expiry = expiryDate
    const daysToExpiry = expiry ? daysBetween(new Date(expiry), today) : null
    const reminder = it.clientId ? reminderByClient.get(it.clientId) : null
    const reminderSentDays = reminder ? daysBetween(today, new Date(reminder.created_at)) : null

    // Month grouping: renewals by expiry month; new by signed month, then the audit date (job-only manual
    // sign-ins have no quote), then created month.
    const monthGroup = type === 'Renewal'
      ? monthKey(expiry)
      : monthKey(quote?.signed_date || quote?.sent_date || job?.audit_date || quote?.created_at)

    rows.push({
      key,
      type,
      clientId: it.clientId,
      jobId: job?.id || null,
      quoteRef: quote?.quote_ref || job?.quote_ref || null,
      client: quoteCompanyName(quote, client) || '—',
      service: quote?.audit_type || job?.audit_type || client?.industry || '—',
      // Current accountable owner (pipeline-layer source of truth). prepared_by is ONLY a suggestion.
      owner: (m.owner ?? m.sales_pic ?? ''),
      ownerSetAt: m.owner_set_at || null,
      ownerSetBy: m.owner_set_by || null,
      suggestedOwner: quote?.prepared_by || '',   // suggestion only — never written automatically
      salesPic: (m.owner ?? m.sales_pic ?? ''),    // transitional alias; remove next release
      contactName: client?.contact_name || quote?.prospect_name || '',
      contactEmail: client?.contact_email || '',
      expiry,
      auditDate,
      lastAuditDate,
      daysToExpiry,
      reminderSentDays,
      monthGroup,
      stage,
      stageRank: stageRank(stage),
      postAudit,
      salesOutcome,        // null = Open
      outcomeAuto,         // true when derived (declined), not user-set
      signedDate: quote?.signed_date || null,
      // For the Tracker's stage detail (Form received / Quoted · date). Display only — no rule above uses them.
      quoteStatus: quote?.status || null,
      quoteSentDate: quote?.sent_date || null,
      quoteCreatedAt: quote?.created_at || null,
    })
  }

  // 4) Active filter: lost & completed leave the active board; linked (secondary) combined members are carried
  //    by the primary and never surface as their own row.
  //    Client-list rule: the Tracker follows real business only — every surviving row is tied to a client
  //    record (renewal, job, linked quote, or a quote identity-matched to a client). Prospect quotes for
  //    companies not in the client list have clientId null (Q:) and drop off — they live on Quotations.
  return rows.filter(r => !LOST_OUTCOMES.includes(r.salesOutcome)
    && !(r.clientId && suppressedClients.has(r.clientId))
    && !!r.clientId)
}

// SINGLE SOURCE OF TRUTH for the "Expiring / overdue (≤30 days)" set — used by BOTH the dashboard KPI
// count and the Tracker filter so they always return the identical records. Rule: a renewal row whose
// bizSAFE cert is overdue OR expires within 30 days. No stage/owner/status over-filtering — every renewal
// row on the active board with an expiry ≤30 days counts (audit done / awaiting report / signed included).
export function expiringRenewals(rows) {
  return (rows || []).filter(r => r.type === 'Renewal' && r.daysToExpiry != null && r.daysToExpiry <= 30)
}

// Shared owner lookup so Clients and the Tracker read ownership identically.
// Returns { owner, ownerSetAt, ownerSetBy } from pipeline_meta for a client (key C:{clientId}).
// Does NOT create rows — unassigned simply returns ''. Falls back to the deprecated sales_pic.
export function ownerForClient(metaRows, clientId) {
  const m = (metaRows || []).find(x => x.key === `C:${clientId}`) || {}
  return { owner: (m.owner ?? m.sales_pic ?? ''), ownerSetAt: m.owner_set_at || null, ownerSetBy: m.owner_set_by || null }
}

// Urgency tier for a renewal row.
export function urgency(row) {
  const d = row.daysToExpiry
  if (d == null) return { tier: 'none', label: '' }
  if (d < 0) return { tier: 'overdue', label: `${Math.abs(d)}d overdue` }
  if (d <= 30) return { tier: 'red', label: `${d}d` }       // unified with expiryTier: ≤30 urgent
  if (d <= 90) return { tier: 'amber', label: `${d}d` }     // unified: 31–90 upcoming (was 60)
  return { tier: 'ok', label: `${d}d` }
}

// Dashboard pipeline-only screams (counts). Each is a cross-stage ACTION GAP — distinct from the
// existing Dashboard sections (QM, auditor compliance, ACK/COI, reminders summary).
export function pipelineScreams(rows) {
  const isRenewal = r => r.type === 'Renewal'
  const noOutcomeOpen = r => !r.salesOutcome   // Open
  return {
    expiring30NotScheduled: rows.filter(r => isRenewal(r) && r.daysToExpiry != null && r.daysToExpiry <= 30 && r.stageRank < stageRank('Scheduled')).length,
    expiring60NoOutcome: rows.filter(r => isRenewal(r) && r.daysToExpiry != null && r.daysToExpiry <= 90 && r.stage === 'Awaiting quote' && noOutcomeOpen(r)).length,
    reminderNoOutcome: rows.filter(r => r.reminderSentDays != null && r.reminderSentDays > REMINDER_NO_OUTCOME_DAYS && r.stage === 'Awaiting quote' && noOutcomeOpen(r)).length,
    signedNoAuditDate: rows.filter(r => r.stage === 'Signed').length,
    auditDoneNoReport: rows.filter(r => r.stage === 'Audit done' && r.postAudit === 'Awaiting report').length,
  }
}
